import { useMouseEventCallback } from '@client/hooks/use-events.hooks';
import {
  closeMenu,
  menuMouseHandler,
  openMenu as openContextMenu,
  useMenuPosition,
} from '@client/hooks/use-menu.hooks';
import { NodonTheme } from '@client/style';
import { KeyboardArrowRight } from '@mui/icons-material';
import { Box, Checkbox, ListItemIcon, SxProps, Tooltip } from '@mui/material';
import { hasDefinedProperties } from '@shared/helpers/object.helpers';
import React, { CSSProperties, FC, useCallback, useMemo } from 'react';

import { EllipsisText } from '@ui/EllipsisText/EllipsisText';
import SelectMenuNestedItem from '../SelectMenu/components/SelectMenuNestedItem';
import KebabButton from './components/KebabButton';
import { KebabButtonProps, KebabMenuProps } from './kebabmenu.model';
import { NodonMenuItemProps, SelectListChildren } from '../menu.model';
import SelectMenu from '../SelectMenu';
import { mergeSx } from '@client/helpers/mui.helpers';

export type { KebabButtonProps, KebabMenuProps };

const kebabMenuLayoutSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 20,
  transition: 'opacity 0.3s',
} as const;

interface Props extends KebabMenuProps {
  /**
   * If no items are provided, the kebab menu will be disabled
   */
  items: NodonMenuItemProps[];
  id: string | number;
  margin?: number | string;

  /**
   * If true, the kebab menu will be hidden but will still take place
   */
  hidden?: boolean;
}

export const KebabMenu: FC<Props> = ({
  items,
  id,
  anchor,
  hidden,
  menuWidth,
  tooltip,
  buttonProps,
  onClose,
  onOpen,
  margin = '6px 8px',
  ...props
}) => {
  if (!id) {
    throw new Error('KebabMenu: menu id is required');
  }
  const position = useMenuPosition(id);

  const openKebab: KebabButtonProps['onClick'] = useMouseEventCallback(
    (e) => {
      const anchorPosition = menuMouseHandler(e);
      const isDefinedPosition = hasDefinedProperties(
        anchorPosition,
        'top',
        'left',
      );
      if (isDefinedPosition) {
        openContextMenu({ anchorPosition, id });
      }
      onOpen?.(e.currentTarget);
    },
    { stopPropagation: true },
  );

  const closeKebab = useCallback(() => {
    onClose?.();
    closeMenu();
  }, [onClose]);

  const renderItems = useCallback<SelectListChildren<NodonMenuItemProps>>(
    (item, style) => <ListItem key={item.id} item={item} style={style} />,
    [],
  );

  return (
    <Box
      className={props.className}
      sx={mergeSx(
        kebabMenuLayoutSx,
        { m: margin, opacity: hidden ? 0 : 1 },
        props.sx,
      )}
    >
      <Tooltip disableInteractive title={tooltip}>
        <Box>
          <KebabButton
            {...buttonProps}
            disabled={items.length === 0 || hidden}
            hidden={hidden}
            onClick={openKebab}
          />
        </Box>
      </Tooltip>
      {(position || anchor) && (
        <SelectMenu
          anchor={anchor}
          anchorPosition={position}
          items={items}
          width={menuWidth === 'anchorEl' ? undefined : menuWidth}
          onClose={closeKebab}
        >
          {renderItems}
        </SelectMenu>
      )}
    </Box>
  );
};

const listItemIconStyles: SxProps = {
  paddingRight: 2,
  minWidth: 0,
} as const;

const arrowIconStyles: SxProps = {
  color: NodonTheme.palette.neutral.light,
} as const;

interface ListItemProps {
  item: NodonMenuItemProps;
  style?: CSSProperties;
}

const ListItem = ({ item, style }: ListItemProps) => {
  const { onClick, tooltip, disabled } = item;

  const handleClick = useCallback(async () => {
    await onClick?.();
    closeMenu();
  }, [onClick]);

  return (
    <SelectMenuNestedItem
      style={style}
      item={item}
      tooltip={tooltip}
      disabled={disabled}
      onClick={handleClick}
    >
      {({ icon, label, checked, checkboxColor, items }) => {
        return (
          <>
            <ListItemIcon sx={listItemIconStyles}>
              {typeof checked === 'boolean' ? (
                <CheckboxButton
                  checked={checked}
                  checkboxColor={checkboxColor}
                />
              ) : (
                icon
              )}
            </ListItemIcon>

            <EllipsisText>{label}</EllipsisText>

            {!!items?.length && <KeyboardArrowRight sx={arrowIconStyles} />}
          </>
        );
      }}
    </SelectMenuNestedItem>
  );
};

interface CheckboxButtonProps {
  checked: boolean;
  checkboxColor?: string;
}

const CheckboxButton = ({ checked, checkboxColor }: CheckboxButtonProps) => {
  const sx = useMemo<SxProps | undefined>(
    () => ({
      '& .MuiSvgIcon-root': {
        fill: checkboxColor,
      },
      padding: 0,
    }),
    [checkboxColor],
  );

  return <Checkbox checked={checked} sx={sx} />;
};
